import { useCallback, useEffect, useState } from 'react';
import { useMembership } from '../../../hooks/useMembership';
import { IDocument, IDocumentsWithMetadata } from '../../send-documents/send/types';
import Icon from '../../common/Icon';
import { NotificationBannerItem } from '../NotificationBanner';
import { StatusColor } from '../StatusBanner';
import useApi from '../../../hooks/useApi';

export interface IExpiredDocument extends IDocument {
  viewLink: string;
}

type DocumentStatus = {
  icon: React.ReactNode;
  status: StatusColor;
  text: React.ReactNode;
  highlight: boolean;
};

const useExpiringDocumentsAlert = () => {
  const [documents, setDocuments] = useState<IExpiredDocument[]>([]);
  const { accountId } = useMembership();
  const { getApi } = useApi();

  const isExpiredOrExpiringSoon = (expiryDate: string | Date): boolean => {
    const expiry = new Date(expiryDate);
    const daysLeft = (expiry.getTime() - Date.now()) / 86400000;
    return daysLeft <= 30;
  };

  const refresh = useCallback(async () => {
    const api = await getApi();

    await api
      .url(`/documents/${accountId}/metadata`)
      .get()
      .json((data: IDocumentsWithMetadata) => {
        const allDocuments: IExpiredDocument[] = [];

        allDocuments.push(
          ...data.documents
            .filter(
              d => isExpiredOrExpiringSoon(d.expiryDate) && d.requireExpiryDateEntry
            )
            .map(d => ({
              ...d,
              viewLink: `/documents/company`,
            }))
        );

        data.units.forEach(unit => {
          allDocuments.push(
            ...unit.documents
              .filter(
                d =>
                  isExpiredOrExpiringSoon(d.expiryDate) &&
                  d.requireExpiryDateEntry
              )
              .map(d => ({
                ...d,
                viewLink: `/unit-premises/${unit.id}/documents`,
              }))
          );
        });

        data.trainingCertificates
          .filter(tc => tc.id !== accountId)
          .forEach(tc => {
            allDocuments.push(
              ...tc.documents
                .filter(d => isExpiredOrExpiringSoon(d.expiryDate))
                .map(d => ({
                  ...d,
                  viewLink: `/training/certificates`,
                }))
            );
          });

        setDocuments(allDocuments);
        return data;
      });
  }, [accountId, getApi]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getDocumentStatus = (
    document: IExpiredDocument,
    daysLeft: number
  ): DocumentStatus => {
    const shouldHighlight = [
      'FireRiskAssessment',
      'HsRiskAssessment',
      'FoodSafetyRiskAssessment',
    ].includes(document.documentType);

    if (daysLeft < 0) {
      return {
        status: 'red',
        icon: <Icon name="warning" color="#c62828" />,
        text: `${document.displayName} - expired ${Math.abs(daysLeft)} days ago`,
        highlight: shouldHighlight,
      };
    }

    return {
      status: 'amber',
      icon: <Icon name="information" color="#f9a825" />,
      text: `${document.displayName} - expires in ${Math.abs(daysLeft)} days`,
      highlight: shouldHighlight,
    };
  };

  const alerts: NotificationBannerItem[] = documents.map(doc => {
    const daysLeft = Math.ceil(
      (new Date(doc.expiryDate).getTime() - Date.now()) / 86400000
    );

    const status = getDocumentStatus(doc, daysLeft);

    return {
      key: doc.displayName,
      status: status.status,
      icon: status.icon,
      text: status.text,
      buttonText: 'View',
      buttonHref: doc.viewLink,
      highlight: status.highlight,
      highlightOrder: status.highlight 
        ? 2 
        : undefined,
    };
  });

  return { alerts, refresh };
};

export default useExpiringDocumentsAlert;